<template>
  <div class="release-history-page">
    <div class="page-header">
      <h1>Frontend Release History</h1>
      <p class="subtitle">Complete changelog for eDoatap External User Interface</p>
    </div>

    <div class="filters">
      <el-input
        v-model="searchQuery"
        placeholder="Search releases..."
        prefix-icon="el-icon-search"
        clearable
        style="width: 300px; margin-right: 16px;"
      />
      <el-select v-model="filterType" placeholder="Filter by type" clearable style="width: 150px;">
        <el-option label="All Types" value="" />
        <el-option label="Feature" value="Feature" />
        <el-option label="Bugfix" value="Bugfix" />
        <el-option label="Update" value="Update" />
        <el-option label="Hotfix" value="Hotfix" />
      </el-select>
    </div>

    <el-timeline class="release-timeline">
      <el-timeline-item
        v-for="release in filteredReleases"
        :key="release.id"
        :timestamp="formatDate(release.build.date)"
        placement="top"
        :type="getTimelineType(release.type)"
        :color="getTypeColor(release.type)"
      >
        <el-card class="release-card" shadow="hover">
          <div class="release-header">
            <div class="release-title-section">
              <h2>{{ release.version }} (build {{ release.build.number }})</h2>
              <el-tag :type="getTagType(release.type)" size="small">{{ release.type }}</el-tag>
            </div>
            <div class="release-id">{{ release.id }}</div>
          </div>

          <h3 class="release-description">{{ release.title }}</h3>

          <el-divider />

          <div class="build-info">
            <div class="info-row">
              <span class="info-label">Build Number:</span>
              <span class="info-value">{{ release.build.number }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Author:</span>
              <span class="info-value">{{ release.build.author }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Build Period:</span>
              <span class="info-value">{{ formatDate(release.build.startDate) }} → {{ formatDate(release.build.date) || 'In Progress' }}</span>
            </div>
          </div>

          <el-divider>Commits ({{ release.build.commits.length }})</el-divider>

          <div class="commits-section">
            <div
              v-for="(commit, index) in release.build.commits"
              :key="index"
              class="commit-item"
            >
              <div class="commit-header">
                <el-tag size="mini" :type="getCommitTagType(commit.type)">{{ commit.type }}</el-tag>
                <span class="commit-message">{{ commit.message }}</span>
              </div>
              <div class="commit-meta">
                <span v-if="commit.sha" class="commit-sha">
                  <i class="el-icon-document-copy"></i> {{ formatSha(commit.sha) }}
                </span>
                <span v-if="commit.date" class="commit-date">
                  <i class="el-icon-time"></i> {{ formatDate(commit.date) }}
                </span>
                <span v-if="commit.issues && commit.issues.length > 0" class="commit-issues">
                  <i class="el-icon-paperclip"></i> {{ formatIssues(commit.issues) }}
                </span>
              </div>
            </div>
          </div>
        </el-card>
      </el-timeline-item>
    </el-timeline>

    <div v-if="filteredReleases.length === 0" class="no-results">
      <i class="el-icon-search"></i>
      <p>No releases found matching your criteria</p>
    </div>
  </div>
</template>

<script>
import { RELEASES } from '../releases.js'

export default {
  name: 'ReleaseHistory',
  data() {
    return {
      releases: RELEASES,
      searchQuery: '',
      filterType: '',
    }
  },
  computed: {
    filteredReleases() {
      let filtered = this.releases

      if (this.filterType) {
        filtered = filtered.filter(r => r.type === this.filterType)
      }

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(r =>
          r.version.toLowerCase().includes(query) ||
          r.title.toLowerCase().includes(query) ||
          r.id.toLowerCase().includes(query) ||
          r.build.commits.some(c => c.message.toLowerCase().includes(query))
        )
      }

      return filtered
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return 'N/A'
      if (date.includes('??')) return date
      return new Date(date).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },
    formatSha(sha) {
      if (!sha) return 'N/A'
      return sha.substring(0, 8)
    },
    formatIssues(issues) {
      if (!issues || issues.length === 0) return ''
      return issues.map(i => `#${i}`).join(', ')
    },
    getTypeColor(type) {
      const colors = {
        Feature: '#67c23a',
        Bugfix: '#f56c6c',
        Hotfix: '#ff4757',
        Update: '#409eff',
        Security: '#e6a23c',
      }
      return colors[type] || '#909399'
    },
    getTagType(type) {
      const types = {
        Feature: 'success',
        Bugfix: 'danger',
        Hotfix: 'danger',
        Update: 'primary',
        Security: 'warning',
      }
      return types[type] || 'info'
    },
    getTimelineType(type) {
      const types = {
        Feature: 'success',
        Bugfix: 'danger',
        Hotfix: 'danger',
        Update: 'primary',
      }
      return types[type] || 'info'
    },
    getCommitTagType(commitType) {
      const types = {
        feat: 'success',
        fix: 'danger',
        style: 'warning',
        chore: 'info',
        i18n: 'primary',
        build: 'info',
      }
      return types[commitType] || 'info'
    },
  },
}
</script>

<style scoped>
.release-history-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 100vh;
  background: linear-gradient(to bottom, #f5f7fa 0%, #ffffff 300px);
}

.page-header {
  text-align: center;
  margin-bottom: 40px;
}

.page-header h1 {
  font-size: 36px;
  font-weight: 600;
  color: #002e69;
  margin: 0 0 8px 0;
}

.subtitle {
  font-size: 16px;
  color: #606266;
  margin: 0;
}

.filters {
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
}

.release-timeline {
  padding: 0 20px;
}

.release-card {
  margin-bottom: 20px;
}

.release-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.release-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.release-title-section h2 {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin: 0;
}

.release-id {
  font-size: 13px;
  color: #909399;
  font-family: monospace;
}

.release-description {
  font-size: 16px;
  color: #606266;
  font-weight: 400;
  margin: 0 0 16px 0;
}

.build-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 12px;
  margin: 16px 0;
}

.info-row {
  display: flex;
  gap: 8px;
}

.info-label {
  font-weight: 600;
  color: #606266;
}

.info-value {
  color: #303133;
}

.commits-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.commit-item {
  padding: 12px;
  background: #f5f7fa;
  border-radius: 4px;
  border-left: 3px solid #409eff;
}

.commit-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.commit-message {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}

.commit-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 12px;
  color: #909399;
  margin-left: 52px;
}

.commit-sha,
.commit-date,
.commit-issues {
  display: flex;
  align-items: center;
  gap: 4px;
}

.commit-sha {
  font-family: monospace;
}

.no-results {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.no-results i {
  font-size: 64px;
  margin-bottom: 16px;
}

.no-results p {
  font-size: 16px;
  margin: 0;
}

@media (max-width: 768px) {
  .release-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .build-info {
    grid-template-columns: 1fr;
  }

  .commit-meta {
    margin-left: 0;
  }
}
</style>
